import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, Textarea } from '@bnvsrnd/brand-assets';
import { MessageCircle, X, ChevronLeft, Send } from 'lucide-react';
import api from '../../utils/api';
import { toast } from '../../utils/toastBus';
import { AuthContext } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';

/**
 * 슈퍼바이저 ↔ 강사 1:1 채팅 — 강사 포털 오른쪽 아래에 떠 있는 아이콘(InstructorLayout 이 깐다).
 * 대화 상대는 수업 일정으로 맺어진 사이만(내 일정의 슈퍼바이저 / 내가 슈퍼바이저인 일정의 강사) — 서버가 정한다.
 * 새 메시지는 소켓 'supervisionChat:message' 로 오고, 보고 있는 대화면 바로 붙이고 읽음 처리한다.
 * 슈퍼바이저 화면의 '메시지' 버튼은 useSupervisionChat().openThread 로 이 창의 그 강사 대화를 연다.
 */
const CHAT_EVENT = 'supervisionChat:message';
const CHAT_READ_EVENT = 'supervisionChat:read';
const RELATION_LABEL = { supervisor: '슈퍼바이저', instructor: '강사', both: '슈퍼바이저 · 강사' };

const SupervisionChatContext = createContext({ openThread: () => {} });
export const useSupervisionChat = () => useContext(SupervisionChatContext);

const timeLabel = (v) => {
    if (!v) return '';
    const d = new Date(v);
    const sameDay = d.toDateString() === new Date().toDateString();
    return sameDay
        ? d.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })
        : d.toLocaleDateString('ko-KR', { month: 'numeric', day: 'numeric' });
};
const fullTime = (v) => new Date(v).toLocaleString('ko-KR', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' });

const panelStyle = {
    position: 'fixed', right: 24, bottom: 92, zIndex: 1040,
    width: 'min(360px, calc(100vw - 32px))', height: 'min(540px, calc(100vh - 120px))',
    display: 'flex', flexDirection: 'column', overflow: 'hidden',
    background: 'var(--background-surface)', border: '1px solid var(--border-default)',
    borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-md)'
};
const headerStyle = {
    display: 'flex', alignItems: 'center', gap: 'var(--space-2)', padding: 'var(--space-3) var(--space-4)',
    borderBottom: '1px solid var(--border-subtle)', minHeight: 56
};
const iconButton = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32,
    border: 'none', background: 'transparent', borderRadius: 'var(--radius-md)', cursor: 'pointer', color: 'var(--text-secondary)'
};

export function SupervisionChatProvider({ children }) {
    const { user } = useContext(AuthContext);
    const { socket } = useNotifications();
    const myId = String(user?._id || user?.id || '');

    const [open, setOpen] = useState(false);
    const [contacts, setContacts] = useState(null);
    const [active, setActive] = useState(null); // { userId, name, canSend }
    const [messages, setMessages] = useState([]);
    const [hasMore, setHasMore] = useState(false);
    const [draft, setDraft] = useState('');
    const [sending, setSending] = useState(false);
    const listRef = useRef(null);
    const activeRef = useRef(null);
    const openRef = useRef(false);
    activeRef.current = active;
    openRef.current = open;

    const loadContacts = useCallback(async () => {
        try {
            const res = await api.get('/supervision/chat/contacts');
            setContacts(res.data.data || []);
        } catch {
            setContacts((prev) => prev || []);
        }
    }, []);

    useEffect(() => { loadContacts(); }, [loadContacts]);

    const scrollToBottom = () => requestAnimationFrame(() => {
        if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    });

    const markRead = useCallback(async (userId) => {
        try {
            await api.post(`/supervision/chat/threads/${userId}/read`);
            setContacts((prev) => (prev || []).map((c) => (c.userId === userId ? { ...c, unread: 0 } : c)));
        } catch { /* 다음에 열 때 다시 처리된다 */ }
    }, []);

    const loadThread = useCallback(async (userId) => {
        const res = await api.get(`/supervision/chat/threads/${userId}`);
        setMessages(res.data.data || []);
        setHasMore(!!res.data.hasMore);
        scrollToBottom();
        markRead(userId);
    }, [markRead]);

    const openThread = useCallback((userId, name) => {
        const contact = (contacts || []).find((c) => c.userId === String(userId));
        setActive({ userId: String(userId), name: contact?.name || name || '', canSend: contact ? contact.canSend : true });
        setDraft('');
        setOpen(true);
        loadThread(String(userId)).catch(() => toast.error('대화를 불러오지 못했습니다.'));
    }, [contacts, loadThread]);

    const loadOlder = async () => {
        if (!active || !messages.length) return;
        const el = listRef.current;
        const prevHeight = el ? el.scrollHeight : 0;
        try {
            const res = await api.get(`/supervision/chat/threads/${active.userId}`, { params: { before: messages[0].createdAt } });
            setMessages((prev) => [...(res.data.data || []), ...prev]);
            setHasMore(!!res.data.hasMore);
            requestAnimationFrame(() => { if (el) el.scrollTop = el.scrollHeight - prevHeight; });
        } catch {
            toast.error('이전 대화를 불러오지 못했습니다.');
        }
    };

    const send = async () => {
        const body = draft.trim();
        if (!body || !active || sending) return;
        setSending(true);
        try {
            const res = await api.post(`/supervision/chat/threads/${active.userId}`, { body });
            const msg = res.data.data;
            setMessages((prev) => (prev.some((m) => m._id === msg._id) ? prev : [...prev, msg]));
            setDraft('');
            scrollToBottom();
            setContacts((prev) => (prev || []).map((c) => (c.userId === active.userId ? { ...c, lastMessage: msg } : c)));
        } catch (err) {
            toast.error(err.response?.data?.message || '메시지를 보내지 못했습니다.');
        } finally {
            setSending(false);
        }
    };

    // 실시간 — 새 메시지 / 상대가 읽음
    useEffect(() => {
        if (!socket) return undefined;
        const onMessage = ({ message, fromName }) => {
            if (!message) return;
            const partnerId = message.from === myId ? message.to : message.from;
            const viewing = openRef.current && activeRef.current?.userId === partnerId;
            if (viewing) {
                setMessages((prev) => (prev.some((m) => m._id === message._id) ? prev : [...prev, message]));
                scrollToBottom();
                if (message.to === myId) markRead(partnerId);
            } else if (message.to === myId) {
                toast.info(`${fromName || '새'} 메시지: ${message.body.slice(0, 40)}`);
            }
            setContacts((prev) => {
                const list = prev || [];
                if (!list.some((c) => c.userId === partnerId)) {
                    loadContacts();
                    return list;
                }
                return list
                    .map((c) => (c.userId !== partnerId ? c : {
                        ...c,
                        lastMessage: message,
                        unread: message.to === myId && !viewing ? c.unread + 1 : c.unread
                    }))
                    .sort((a, b) => new Date(b.lastMessage?.createdAt || 0) - new Date(a.lastMessage?.createdAt || 0));
            });
        };
        const onRead = ({ readerId, partnerId, readAt }) => {
            if (readerId !== myId && activeRef.current?.userId === readerId) {
                setMessages((prev) => prev.map((m) => (m.from === myId && !m.readAt ? { ...m, readAt } : m)));
            }
            if (readerId === myId) {
                setContacts((prev) => (prev || []).map((c) => (c.userId === partnerId ? { ...c, unread: 0 } : c)));
            }
        };
        socket.on(CHAT_EVENT, onMessage);
        socket.on(CHAT_READ_EVENT, onRead);
        return () => {
            socket.off(CHAT_EVENT, onMessage);
            socket.off(CHAT_READ_EVENT, onRead);
        };
    }, [socket, myId, markRead, loadContacts]);

    const unreadTotal = useMemo(() => (contacts || []).reduce((n, c) => n + (c.unread || 0), 0), [contacts]);
    const lastMineReadId = useMemo(() => {
        const mine = messages.filter((m) => m.from === myId && m.readAt);
        return mine.length ? mine[mine.length - 1]._id : null;
    }, [messages, myId]);

    const contextValue = useMemo(() => ({ openThread }), [openThread]);
    const visible = contacts && contacts.length > 0;

    return (
        <SupervisionChatContext.Provider value={contextValue}>
            {children}
            {visible && (
                <>
                    <button
                        type="button"
                        aria-label={open ? '채팅 닫기' : `슈퍼바이저 채팅 열기${unreadTotal ? ` (안 읽은 메시지 ${unreadTotal}개)` : ''}`}
                        onClick={() => setOpen((v) => !v)}
                        style={{
                            position: 'fixed', right: 24, bottom: 24, zIndex: 1040, width: 56, height: 56, borderRadius: '50%',
                            border: 'none', cursor: 'pointer', background: 'var(--action-primary-default)', color: 'var(--text-inverse)',
                            boxShadow: 'var(--shadow-md)', display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}
                    >
                        {open ? <X size={24} /> : <MessageCircle size={24} />}
                        {!open && unreadTotal > 0 && (
                            <span style={{
                                position: 'absolute', top: -2, right: -2, minWidth: 20, height: 20, padding: '0 6px', borderRadius: 10,
                                background: 'var(--action-destructive-default)', color: 'var(--text-inverse)', font: 'var(--label-small-semibold)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}>
                                {unreadTotal > 99 ? '99+' : unreadTotal}
                            </span>
                        )}
                    </button>

                    {open && (
                        <div style={panelStyle} role="dialog" aria-label="슈퍼바이저 채팅">
                            {!active ? (
                                <>
                                    <div style={headerStyle}>
                                        <strong style={{ font: 'var(--label-default-semibold)', color: 'var(--text-primary)', flex: 1 }}>메시지</strong>
                                        <button type="button" style={iconButton} onClick={() => setOpen(false)} aria-label="닫기"><X size={18} /></button>
                                    </div>
                                    <div style={{ overflowY: 'auto', flex: 1 }}>
                                        {contacts.map((c) => (
                                            <button
                                                key={c.userId}
                                                type="button"
                                                onClick={() => openThread(c.userId, c.name)}
                                                style={{
                                                    display: 'flex', width: '100%', gap: 'var(--space-3)', alignItems: 'flex-start', textAlign: 'left',
                                                    padding: 'var(--space-3) var(--space-4)', border: 'none', borderBottom: '1px solid var(--border-subtle)',
                                                    background: 'transparent', cursor: 'pointer'
                                                }}
                                            >
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                                        <span style={{ font: 'var(--label-default-semibold)', color: 'var(--text-primary)' }}>{c.name}</span>
                                                        {c.relation && <Badge variant="soft" size="sm" color={c.relation === 'instructor' ? 'neutral' : 'primary'}>{RELATION_LABEL[c.relation]}</Badge>}
                                                        <span style={{ marginLeft: 'auto', font: 'var(--caption)', color: 'var(--text-tertiary)' }}>{timeLabel(c.lastMessage?.createdAt)}</span>
                                                    </div>
                                                    <div style={{ font: 'var(--body-small)', color: 'var(--text-tertiary)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                        {c.lastMessage ? c.lastMessage.body : (c.schools.join(', ') || '대화를 시작해 보세요')}
                                                    </div>
                                                </div>
                                                {c.unread > 0 && <Badge color="danger" size="sm">{c.unread}</Badge>}
                                            </button>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div style={headerStyle}>
                                        <button type="button" style={iconButton} onClick={() => { setActive(null); loadContacts(); }} aria-label="목록으로"><ChevronLeft size={18} /></button>
                                        <strong style={{ font: 'var(--label-default-semibold)', color: 'var(--text-primary)', flex: 1 }}>{active.name}</strong>
                                        <button type="button" style={iconButton} onClick={() => setOpen(false)} aria-label="닫기"><X size={18} /></button>
                                    </div>
                                    <div ref={listRef} style={{ flex: 1, overflowY: 'auto', padding: 'var(--space-3) var(--space-4)', background: 'var(--background-subtle)', display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                                        {hasMore && (
                                            <button type="button" onClick={loadOlder} style={{ alignSelf: 'center', border: 'none', background: 'transparent', color: 'var(--text-tertiary)', font: 'var(--caption)', cursor: 'pointer' }}>
                                                이전 대화 더 보기
                                            </button>
                                        )}
                                        {messages.length === 0 && (
                                            <div style={{ margin: 'auto', color: 'var(--text-tertiary)', font: 'var(--body-small)' }}>아직 대화가 없습니다.</div>
                                        )}
                                        {messages.map((m) => {
                                            const mine = m.from === myId;
                                            return (
                                                <div key={m._id} style={{ display: 'flex', flexDirection: 'column', alignItems: mine ? 'flex-end' : 'flex-start' }}>
                                                    <div
                                                        title={fullTime(m.createdAt)}
                                                        style={{
                                                            maxWidth: '78%', padding: '8px 12px', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                                                            font: 'var(--body-small)', lineHeight: 1.5,
                                                            borderRadius: mine ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                                                            background: mine ? 'var(--action-primary-default)' : 'var(--background-surface)',
                                                            color: mine ? 'var(--text-inverse)' : 'var(--text-primary)',
                                                            border: mine ? 'none' : '1px solid var(--border-subtle)'
                                                        }}
                                                    >
                                                        {m.body}
                                                    </div>
                                                    <span style={{ font: 'var(--caption)', color: 'var(--text-tertiary)', marginTop: 2 }}>
                                                        {timeLabel(m.createdAt)}{mine && m._id === lastMineReadId ? ' · 읽음' : ''}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {active.canSend ? (
                                        <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'flex-end', padding: 'var(--space-3)', borderTop: '1px solid var(--border-subtle)' }}>
                                            <div style={{ flex: 1 }}>
                                                <Textarea
                                                    rows={2}
                                                    maxLength={2000}
                                                    value={draft}
                                                    onChange={(e) => setDraft(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                                                            e.preventDefault();
                                                            send();
                                                        }
                                                    }}
                                                    placeholder="메시지 입력 (Enter 보내기, Shift+Enter 줄바꿈)"
                                                    aria-label="메시지 입력"
                                                />
                                            </div>
                                            <Button variant="primary" onClick={send} disabled={sending || !draft.trim()} aria-label="보내기">
                                                <Send size="1em" />
                                            </Button>
                                        </div>
                                    ) : (
                                        <div style={{ padding: 'var(--space-3) var(--space-4)', borderTop: '1px solid var(--border-subtle)', font: 'var(--body-small)', color: 'var(--text-tertiary)' }}>
                                            지금은 같은 수업 일정이 없어 메시지를 보낼 수 없습니다(지난 대화만 볼 수 있음).
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </>
            )}
        </SupervisionChatContext.Provider>
    );
}

export default SupervisionChatProvider;
